'use client';

import { useEffect, useSyncExternalStore } from 'react';

import {
  APP_PLATFORM_KEY,
  APP_VERSION_KEY,
  type AppShellSignals,
  isAndroidAppShell,
  isIosAppShell,
} from './appShell';
import { useAppShellSeed } from './appShellSeed';

/**
 * localStorage key for the native app version. Mirrors PopshopRN's
 * `kAppVersionKey` — the native shell injects `?appVersion=` on the entry URL,
 * and we persist it so it survives in-app navigations and logout (the shell
 * drops the param after the first load, and the logout redirect omits it). Once
 * a user loads popstore-next from the app, they stay an app user for the whole
 * session.
 *
 * Declared in `appShell.ts` — the URL param name and the storage key are one
 * identity, and the SERVER-side resolver needs the same constant. Re-exported
 * here because this is the module every consumer already imports it from.
 */
export const APP_VERSION_STORAGE_KEY = APP_VERSION_KEY;

/**
 * Read the native app version the shell injected, WITHOUT persisting. Pure: safe
 * to call from a `useSyncExternalStore` `getSnapshot`. 1:1 with PopshopRN's
 * `useAppVersion` read path:
 *   - sanitize the query string with `.replaceAll('?', '&')` — older shell
 *     builds emit a malformed double `?` (e.g. `…?reg=creator?appVersion=…`);
 *   - read `?appVersion=`, else fall back to the persisted value.
 * Returns null on the server / plain web.
 */
function peekAppVersion(): string | null {
  if (typeof window === 'undefined') return null;
  // Fix double '?' in query string for older app versions (parity with legacy).
  const queryString = window.location.search.replaceAll('?', '&');
  const fromUrl = new URLSearchParams(queryString).get(APP_VERSION_STORAGE_KEY);
  if (fromUrl) return fromUrl;
  try {
    return localStorage.getItem(APP_VERSION_STORAGE_KEY);
  } catch {
    // localStorage unavailable
    return null;
  }
}

/**
 * Read the app version AND persist a URL-provided value on first sight (the
 * shell drops the `?appVersion=` param after entry, so we cache it to survive
 * later in-app navigations and logout). Has a side effect — use {@link
 * peekAppVersion} for render-phase reads. Returns null on the server / plain web.
 */
function readAppVersion(): string | null {
  const version = peekAppVersion();
  if (typeof window !== 'undefined' && version) {
    try {
      if (localStorage.getItem(APP_VERSION_STORAGE_KEY) !== version) {
        localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
      }
    } catch {
      // localStorage unavailable
    }
  }
  return version;
}

/**
 * No-op subscribe for {@link useIsAppUser} / {@link useMobileAppVersion}. The
 * `appVersion` signal is written once at entry ({@link useCaptureAppVersion})
 * and never flips during a session, so there is nothing to subscribe to. React
 * still transitions from the server snapshot to the client snapshot once, after
 * hydration completes — which is exactly the SSR-safe behavior we want.
 */
function subscribeAppVersion(): () => void {
  return () => {};
}

/**
 * Persist the shell-injected `?appVersion=` to `localStorage` at app entry.
 *
 * The native shell only puts `appVersion` on the ENTRY url (`/login?…`). By the
 * time any creator surface (`NativeAppBridge`, aside nav, …) mounts on
 * `/dashboard`, the param is gone from the URL — so `readAppVersion()` there
 * finds nothing to persist and `isAppUser` reads `false`, silently disabling the
 * whole native bridge. Mounting this ONCE at the entry (the login layout) reads
 * the param while it's still present and persists it, so every later route sees
 * a reliable `isAppUser`/`getIsAppUser`. No-op on the plain web (no param) and
 * for a returning user (value already persisted).
 */
export function useCaptureAppVersion(): void {
  useEffect(() => {
    readAppVersion();
    // Same one-shot capture as the version: the shell puts `appPlatform` only on
    // the entry URL, so persist it while it is still there.
    const platform = peekAppPlatform();
    if (!platform) return;
    try {
      if (localStorage.getItem(APP_PLATFORM_STORAGE_KEY) !== platform) {
        localStorage.setItem(APP_PLATFORM_STORAGE_KEY, platform);
      }
    } catch {
      // localStorage unavailable
    }
  }, []);
}

/**
 * The native app version string (e.g. `"1.3.0"`) or null when not in the app.
 * Use for version-gated capabilities (native share, Google Calendar, …), 1:1
 * with PopshopRN's `useMobileAppVersion`.
 */
export function useMobileAppVersion(): string | null {
  // `useSyncExternalStore` uses the server snapshot (null) for the server render
  // AND the initial hydration render, so SSR stays in sync; the real value from
  // the client snapshot lands right after hydration. (`peekAppVersion` is pure —
  // persistence is handled once by `useCaptureAppVersion` at entry.)
  return useSyncExternalStore(subscribeAppVersion, peekAppVersion, () => null);
}

/**
 * `window.ReactNativeWebView`, the shell's bridge — injected by the WebView, so
 * it can be neither carried in a URL nor persisted. Pure.
 */
function peekNativeShell(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    (window as unknown as { ReactNativeWebView?: unknown })
      .ReactNativeWebView != null
  );
}

/**
 * Are we inside the native app? `appVersion` (URL param → persisted fallback)
 * OR the live bridge. The shell never sends `?isAppUser=`.
 *
 * The bridge is ORed in because `appVersion` is captured on ONE route
 * (`AppVersionCapture`, the `/login` layout): lose that value — evicted storage
 * (WebKit caps it at 7 days), a cleared store, an entry that skipped `/login` —
 * and this read false INSIDE the app, which fails every gate open, payment CTAs
 * included. `appVersion` still leads: it survives the in-app navigations, and it
 * is what the iOS/Android split reads.
 *
 * SSR-safe via `useSyncExternalStore`: the server snapshot (`false`) covers the
 * server render AND hydration, then React takes the client snapshot — so an
 * app-only gate paints the web variant for one frame, which is the cost of not
 * hydration-mismatching. Outside React use {@link getIsAppUser}.
 */
export function useIsAppUser(): boolean {
  return useSyncExternalStore(
    subscribeAppVersion,
    () => peekAppVersion() !== null || peekNativeShell(),
    () => false,
  );
}

/**
 * Non-hook {@link useIsAppUser}, for event handlers and util modules. Inside a
 * component/hook use the hook so the value stays reactive.
 */
export function getIsAppUser(): boolean {
  return readAppVersion() !== null || peekNativeShell();
}

/**
 * localStorage key for the native platform the shell is running on. Mirrors
 * {@link APP_VERSION_STORAGE_KEY}: the shell injects `?appPlatform=` on the
 * entry URL and drops it after the first load, so it is persisted the same way.
 */
export const APP_PLATFORM_STORAGE_KEY = APP_PLATFORM_KEY;

/** `'ios' | 'android'` as the shell reported it, or null. Pure. */
function peekAppPlatform(): string | null {
  if (typeof window === 'undefined') return null;
  const queryString = window.location.search.replaceAll('?', '&');
  const fromUrl = new URLSearchParams(queryString).get(
    APP_PLATFORM_STORAGE_KEY,
  );
  if (fromUrl) return fromUrl;
  try {
    return localStorage.getItem(APP_PLATFORM_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * The shell signals as the CLIENT can see them — URL param, else the persisted
 * value, plus the WebView's user agent. Pure, so it is safe from a
 * `getSnapshot`. The predicates over it live in `appShell.ts` because the
 * SERVER has to reach the same verdict from `searchParams` + the request's UA;
 * see {@link useAppShellSeed}.
 */
function peekAppShellSignals(): AppShellSignals {
  return {
    version: peekAppVersion(),
    platform: peekAppPlatform(),
    userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
  };
}

/**
 * Is this an app user on iOS? {@link isIosAppShell} against what the client can
 * see.
 */
function peekIsIosAppUser(): boolean {
  return isIosAppShell(peekAppShellSignals());
}

/** Is this an app user on Android? {@link isAndroidAppShell}, likewise. */
function peekIsAndroidAppUser(): boolean {
  return isAndroidAppShell(peekAppShellSignals());
}

/**
 * App user AND iOS. Use this — not bare {@link useIsAppUser} — to gate anything
 * that depends on an iOS-only shell capability (Apple Pay, StoreKit), so the
 * Android shell is never handed a message it has no handler for.
 *
 * SSR-safe the same way as {@link useIsAppUser}: the server snapshot is used
 * for the server render AND the hydration render, the real value lands after
 * hydration. Under an {@link AppShellSeedProvider} that server snapshot is the
 * verdict the server itself reached, so a seeded surface never paints the wrong
 * variant at all.
 */
export function useIsIosAppUser(): boolean {
  const seed = useAppShellSeed();
  return useSyncExternalStore(subscribeAppVersion, peekIsIosAppUser, () =>
    seed ? isIosAppShell(seed) : false,
  );
}

/** Non-hook variant, for event handlers and util modules. */
export function getIsIosAppUser(): boolean {
  return peekIsIosAppUser();
}

/**
 * App user AND Android — the shell to keep an iOS-only capability AWAY from.
 *
 * The complement of {@link useIsIosAppUser} is not this: `!isIosAppUser` is
 * also true on the plain web and for an unknown shell. Suppressing UI needs the
 * question asked positively, and needs the answer at FIRST PAINT — a control
 * that renders and is then pulled reads as a broken page. So gate on this and
 * seed the tree ({@link AppShellSeedProvider}) wherever the server can see the
 * shell's params.
 */
export function useIsAndroidAppUser(): boolean {
  const seed = useAppShellSeed();
  return useSyncExternalStore(subscribeAppVersion, peekIsAndroidAppUser, () =>
    seed ? isAndroidAppShell(seed) : false,
  );
}

/** Non-hook variant, for event handlers and util modules. */
export function getIsAndroidAppUser(): boolean {
  return peekIsAndroidAppUser();
}
